import { useRouter } from 'next/router';
import { useState } from 'react';

import { FeedbackModal } from '@/components/Modal/FeedBackFormModal';

import { ADVANCED_ORDER_TYPE_LABELS, type AdvancedOrderType } from '@/constants/advancedOrder';
import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';

import { AnimatedDiv } from '@/utils/animated-div';
import { ChainAddress } from '@/utils/models';
import { routes } from '@/utils/routes';

import { FeedbackAnimation } from './FeedbackAnimation';
import { FeedbackToast } from './FeedbackToast';
import { ToastContainer, ToastTitle, ToastContent, useAutoDismiss } from './ToastComponents';
import { useFeedbackToastAnimation } from './useFeedbackToastAnimation';

type LimitOrderCheckoutStep =
  | 'submitted'
  | 'cancelPending'
  | 'userCancelled'
  | 'success'
  | 'error'
  | 'submissionFailed';

export interface LimitOrderToastProps {
  checkoutStep: LimitOrderCheckoutStep;
  sellAmount: string;
  buyAmount: string;
  sellToken: {
    address: ChainAddress;
    chainId: number;
    symbol: string;
  };
  buyToken: {
    address: ChainAddress;
    chainId: number;
    symbol: string;
  };
  orderType?: AdvancedOrderType;
  onClose?: () => void;
}

function getLimitOrderToastTitle(
  checkoutStep: LimitOrderCheckoutStep,
  orderType: AdvancedOrderType,
): string {
  const label = ADVANCED_ORDER_TYPE_LABELS[orderType];
  switch (checkoutStep) {
    case 'success':
      return `${label} order canceled!`;
    case 'cancelPending':
      return `Canceling ${label} order...`;
    case 'error':
      return `Couldn't cancel ${label} order`;
    case 'submissionFailed':
      return `Couldn't place ${label} order`;
    case 'userCancelled':
      return 'Transaction canceled';
    case 'submitted':
      return `${label} order placed!`;
    default: {
      const exhaustiveStep: never = checkoutStep;
      throw new Error(`Unknown checkout step: ${exhaustiveStep}`);
    }
  }
}

/**
 * Toast for limit orders (submitted/cancelled)
 */
export function LimitOrderToast({
  checkoutStep,
  sellAmount,
  buyAmount,
  sellToken,
  buyToken,
  orderType = 'limit',
  onClose,
}: LimitOrderToastProps) {
  const router = useRouter();
  const [isHovered, setIsHovered] = useState(false);

  const chainName = NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId];

  const isSubmitted = checkoutStep === 'submitted';
  const isCancelPending = checkoutStep === 'cancelPending';
  const isError =
    checkoutStep === 'userCancelled' ||
    checkoutStep === 'error' ||
    checkoutStep === 'submissionFailed';

  // Use feedback toast animation hook
  const {
    feedbackToastSpring,
    mainToastSpring: limitOrderToastSpring,
    showFeedbackToast,
    showMainToast: showLimitOrderToast,
    showFeedbackModal,
    handleFeedbackToastClose,
    handleFeedbackClick,
    handleMainToastClose: handleLimitOrderToastClose,
    handleFeedbackModalClose,
    setShowFeedbackModal,
  } = useFeedbackToastAnimation({
    isSuccess: isSubmitted,
    onClose,
    chainId: sellToken.chainId,
  });

  const handleViewOpenOrder = () => {
    router.push({
      pathname: routes.TRADE_HISTORY,
      query: {
        tab: 'open-orders',
      },
    });
  };

  // Pending cancellations persist until the receipt resolves (opt out of auto-dismiss).
  useAutoDismiss(isHovered, handleLimitOrderToastClose, isCancelPending);

  const title = getLimitOrderToastTitle(checkoutStep, orderType);

  return (
    <>
      {showFeedbackToast && (
        <AnimatedDiv style={feedbackToastSpring} className="mb-3">
          <FeedbackToast onClose={handleFeedbackToastClose} onFeedbackClick={handleFeedbackClick} />
        </AnimatedDiv>
      )}
      {showLimitOrderToast && (
        <AnimatedDiv style={limitOrderToastSpring}>
          <ToastContainer
            onClose={handleLimitOrderToastClose}
            showProgressAnimation={isCancelPending}
            onHoverChange={setIsHovered}
          >
            {/* Status Icon with Animation */}
            <FeedbackAnimation
              feedback={isCancelPending ? undefined : isError ? 'error' : 'success'}
              className="shrink-0 relative z-10"
            ></FeedbackAnimation>

            {/* Content */}
            <div className="flex flex-col gap-1 min-w-0 flex-1 relative z-10">
              <ToastTitle>{title}</ToastTitle>

              <ToastContent>
                <div className="flex items-center whitespace-nowrap">
                  <span
                    className={fontStyles({
                      variant: 'LABEL_MD',
                      class: ['text-token-gray-1600', 'truncate', 'min-w-0', 'block'],
                    })}
                  >
                    {sellAmount} {sellToken.symbol} to {buyAmount} {buyToken.symbol}
                  </span>
                  {chainName && (
                    <>
                      <span
                        className={fontStyles({
                          variant: 'LABEL_MD',
                          class: ['text-token-gray-700'],
                        })}
                      >
                        &nbsp;on&nbsp;
                      </span>
                      <div className="flex items-center shrink-0">
                        <ChainAvatar
                          chainId={sellToken.chainId}
                          alt={chainName}
                          size={16}
                          className="mr-1"
                        />
                        <span
                          className={fontStyles({
                            variant: 'LABEL_MD',
                            class: ['text-token-gray-700', 'whitespace-nowrap'],
                          })}
                        >
                          {chainName}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </ToastContent>

              {/* View Open Order Button */}
              {isSubmitted && (
                <div className="mt-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewOpenOrder();
                    }}
                    className={fontStyles({
                      variant: 'LABEL_MD',
                      class: [
                        'text-token-purple-900',
                        'cursor-pointer',
                        'p-1.5',
                        '-ml-1',
                        'rounded-[8px]',
                        'hover:bg-token-purple-200',
                        'active:bg-token-purple-200',
                        'focus:outline-none',
                        'focus-visible:ring-2',
                        'focus-visible:ring-token-purple-500',
                        'transition-all',
                        'duration-150',
                        'ease-in-out',
                      ],
                    })}
                    type="button"
                  >
                    View open order
                  </button>
                </div>
              )}
            </div>
          </ToastContainer>
        </AnimatedDiv>
      )}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onOpenChange={setShowFeedbackModal}
        onClose={handleFeedbackModalClose}
        triggerSource="toast"
        chainId={String(sellToken.chainId)}
      />
    </>
  );
}
